import { useTranslation } from 'react-i18next'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  SalesBriefingSection,
  StickyCtaBar,
  BriefingApplicationModal,
  FranchiseInquiryModal,
} from '@marketing/components'
import { useHashScroll } from '@/hooks/useHashScroll'
import { FranchiseHero } from '@/components/home/franchise/FranchiseHero'
import { MarketDataSection } from '@/components/home/franchise/MarketDataSection'
import { ParentConcernSection } from '@/components/home/franchise/ParentConcernSection'
import { ContentPowerSection } from '@/components/home/franchise/ContentPowerSection'
import { QuetureMarqueeSection } from '@/components/home/franchise/QuetureMarqueeSection'
import { SystemDiagramSection } from '@/components/home/franchise/SystemDiagramSection'
import { EarlyBirdSection } from '@/components/home/franchise/EarlyBirdSection'
import { ProcessSection } from '@/components/home/franchise/ProcessSection'
import { getFranchiseContent } from './franchise.content'

export default function FranchisePage() {
  // Rebuild copy on language changes; asset URLs and business values stay unchanged.
  const { t: mt } = useTranslation('marketing')
  const c = getFranchiseContent(mt)
  const navigate = useNavigate()
  const [briefingOpen, setBriefingOpen] = useState(false)
  const [franchiseOpen, setFranchiseOpen] = useState(false)

  const handleBriefing = () => setBriefingOpen(true)
  const handleInquiry = () => setFranchiseOpen(true)

  useHashScroll()

  return (
    // `break-keep` (word-break: keep-all) prevents the default CJK behaviour
    // of breaking Korean text between any two characters (e.g. "설계하세 / 요").
    // Inherited by every descendant section, so all Korean prose wraps at
    // word boundaries rather than mid-word.
    <div className="break-keep">
      <FranchiseHero
        label={c.hero.label}
        body={c.hero.body}
        emphasis={c.hero.emphasis}
        imageUrl={c.hero.imageUrl}
        imageUrlMobile={c.hero.imageUrlMobile}
        imageAlt={c.hero.imageAlt}
      />

      <MarketDataSection
        headerLine1={c.marketData.headerLine1}
        headerLine2={c.marketData.headerLine2}
        titleLine1={c.marketData.titleLine1}
        titleLine2={c.marketData.titleLine2}
        body={c.marketData.body}
        emphasis={c.marketData.emphasis}
        chartUrl={c.marketData.chartUrl}
        chartUrlMobile={c.marketData.chartUrlMobile}
        chartAlt={c.marketData.chartAlt}
      />

      <ParentConcernSection
        headerLine1={c.parentConcern.headerLine1}
        headerLine2={c.parentConcern.headerLine2}
        titleRows={c.parentConcern.titleRows}
        body={c.parentConcern.body}
        emphasis={c.parentConcern.emphasis}
        imageUrl={c.parentConcern.imageUrl}
        imageUrlMobile={c.parentConcern.imageUrlMobile}
        imageAlt={c.parentConcern.imageAlt}
      />

      <ContentPowerSection
        titleLine1={c.contentPower.titleLine1}
        titleLine2Plain={c.contentPower.titleLine2Plain}
        titleLine2Highlight={c.contentPower.titleLine2Highlight}
        cards={c.contentPower.cards}
      />

      <QuetureMarqueeSection
        bodyLine1={c.quetureMarquee.bodyLine1}
        bodyLine2={c.quetureMarquee.bodyLine2}
        emphasis={c.quetureMarquee.emphasis}
        row1={c.quetureMarquee.row1}
        row2={c.quetureMarquee.row2}
      />

      <SystemDiagramSection
        titleLine1={c.systemDiagram.titleLine1}
        titleLine2={c.systemDiagram.titleLine2}
        bodyLine1={c.systemDiagram.bodyLine1}
        bodyLine2={c.systemDiagram.bodyLine2}
        emphasisLine1={c.systemDiagram.emphasisLine1}
        emphasisLine2={c.systemDiagram.emphasisLine2}
        ctaLabel={c.systemDiagram.ctaLabel}
        onCtaClick={() => navigate('/about/lc#learning-step')}
        imageUrl={c.systemDiagram.imageUrl}
        imageUrlMobile={c.systemDiagram.imageUrlMobile}
        imageAlt={c.systemDiagram.imageAlt}
      />

      <EarlyBirdSection
        titleLine1={c.earlyBird.titleLine1}
        titleLine2={c.earlyBird.titleLine2}
        bodyLine1={c.earlyBird.bodyLine1}
        bodyLine2={c.earlyBird.bodyLine2}
        ctaLabel={c.earlyBird.ctaLabel}
        onCtaClick={handleInquiry}
        imageUrl={c.earlyBird.imageUrl}
        imageAlt={c.earlyBird.imageAlt}
      />

      <SalesBriefingSection onBriefingClick={handleBriefing} />

      <ProcessSection
        titleLine1={c.process.titleLine1}
        titleLine2={c.process.titleLine2}
        steps={c.process.steps}
        note={c.process.note}
      />

      <StickyCtaBar
        onBriefingClick={handleBriefing}
        onInquiryClick={handleInquiry}
      />

      <BriefingApplicationModal open={briefingOpen} onClose={() => setBriefingOpen(false)} />
      <FranchiseInquiryModal open={franchiseOpen} onClose={() => setFranchiseOpen(false)} />
    </div>
  )
}
